import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  setConsent, 
  hasConsent, 
  CONSENT_LEVELS 
} from '../../utils/cookieConsent';
import { initAnalytics } from '../../firebase';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    if (!hasConsent()) {
      // Small delay for better UX
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    setConsent(CONSENT_LEVELS.ALL);
    initAnalytics(); // Initialize analytics immediately
    setShowBanner(false);
  };

  const handleEssentialOnly = () => {
    setConsent(CONSENT_LEVELS.ESSENTIAL);
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        style={{ pointerEvents: 'auto' }}
      >
        <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-2xl border border-gray-200 relative">
          <div className="p-4 md:p-6 pr-10">
            {/* Header */}
            <div className="flex items-start mb-3">
              <div className="flex-shrink-0 text-2xl mr-2">🍪</div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-1.5">
                  We value your privacy
                </h3>
                
                {!showDetails ? (
                  <p className="text-gray-700 text-xs md:text-sm leading-relaxed">
                    We use cookies and similar technologies to enhance your browsing experience, analyze traffic, and for B2B visitor identification. 
                    By clicking "Accept All", you consent to our use of cookies. 
                    {' '}
                    <Link to="/privacy" className="text-blue-600 hover:text-blue-800 underline">
                      Learn more in our Privacy Policy
                    </Link>
                    .
                  </p>
                ) : (
                  <div className="text-xs md:text-sm">
                    <p className="text-gray-700 mb-3">
                      We use cookies to provide you with the best experience. Here's what we use:
                    </p>
                    
                    {/* Essential Cookies */}
                    <div className="mb-2 p-2.5 bg-gray-50 rounded">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-gray-900 text-sm">Essential Cookies</h4>
                        <span className="text-xs text-gray-500 font-medium">Always Active</span>
                      </div>
                      <p className="text-xs text-gray-600">
                        Required for the website to function properly. These cannot be disabled.
                      </p>
                    </div>

                    {/* Analytics Cookies */}
                    <div className="mb-2 p-2.5 bg-blue-50 rounded border border-blue-100">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-gray-900 text-sm">Analytics Cookies</h4>
                        <span className="text-xs text-blue-600 font-medium">Optional</span>
                      </div>
                      <p className="text-xs text-gray-600">
                        Help us understand how visitors use our site (via Firebase Analytics). 
                        This helps us improve your experience.
                      </p>
                    </div>

                    {/* B2B Visitor Identification */}
                    <div className="mb-2 p-2.5 bg-gray-50 rounded border border-gray-200">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-gray-900 text-sm">B2B Visitor Identification</h4>
                        <span className="text-xs text-gray-500 font-medium">Optional</span>
                      </div>
                      <p className="text-xs text-gray-600">
                        Our data partners (e.g. RB2B) may use cookies and similar technologies to identify visiting companies. 
                        Opt-out options are in our{' '}
                        <Link to="/privacy" className="text-blue-600 hover:text-blue-800 underline">
                          Privacy Policy
                        </Link>.
                      </p>
                    </div>

                    <p className="text-xs text-gray-500 mt-3">
                      <Link to="/privacy" className="text-blue-600 hover:text-blue-800 underline">
                        Read our full Privacy Policy
                      </Link>
                      {' '}for more information.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <button
                onClick={handleEssentialOnly}
                className="flex-1 px-3 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-md font-medium transition-colors duration-200 text-xs md:text-sm"
              >
                Reject All
              </button>
              
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex-1 px-3 py-2 text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md font-medium transition-colors duration-200 text-xs md:text-sm"
              >
                {showDetails ? 'Hide Details' : 'Customize'}
              </button>
              
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-3 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md font-semibold transition-colors duration-200 shadow-sm text-xs md:text-sm"
              >
                Accept All
              </button>
            </div>

            {/* Close button (small X for mobile) */}
            <button
              onClick={handleEssentialOnly}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close banner and reject non-essential cookies"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieBanner;

